import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  BookOpen,
  Calendar,
  DollarSign,
  ExternalLink,
  FileText,
  Film,
  Heart,
  Loader2,
  LogIn,
  MessageCircle,
  Moon,
  Search,
  Sparkles,
  Sun,
  X,
  Youtube,
} from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../services/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import {
  getOfflineThumbnailCacheUrl,
  listOfflineSessions,
  type OfflineSessionSummary,
} from '../services/offlineSessionStore';
import PdfBadgeIcon from './PdfBadgeIcon';

interface HomepageVideo {
  shareToken: string;
  sessionId: string;
  youtubeUrl: string | null;
  videoSource?: 'youtube' | 'dropbox' | 'file' | null;
  title: string;
  thumbnailUrl: string | null;
  createdAt: string;
  publishedAt?: string | null;
  description: string | null;
  hasPdf: boolean;
  matchedFields?: string[];
  searchPreview?: string | null;
}

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = process.env.REACT_APP_API_URL || (isDevelopment ? 'http://localhost:7777' : '');
const HOME_TITLE = 'Machine Learning Street Talk | ReScript Editorial Transcript Archive';
const HOME_DESCRIPTION =
  'Machine Learning Street Talk ReScript is the editorial transcript archive for long-form AI conversations: searchable transcripts, episode notes, and downloadable PDF editions.';
const HOME_SOCIAL_DESCRIPTION =
  'Search, read, and export Machine Learning Street Talk interviews through the MLST editorial transcript archive.';
const HOME_SOCIAL_IMAGE = 'https://files.rescript.info/mlst-og-image.png';
const LICENSING_EMAIL = String.fromCharCode(116, 105, 109, 64, 109, 108, 115, 116, 46, 97, 105);
const SEARCH_MATCH_LABELS: Record<string, string> = {
  title: 'Title',
  transcript: 'Transcript',
  speakers: 'Speakers',
  topics: 'Topics',
  ttml_topics: 'Topics',
};

const stripMarkdown = (value: string): string =>
  value
    .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_>#~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const truncateText = (value: string, maxLength: number): string =>
  value.length > maxLength ? `${value.slice(0, maxLength).trimEnd()}...` : value;

const getDescriptionPreview = (description: string | null, maxLength = 180): string => {
  if (!description) {
    return 'Open the transcript, skim the editorial summary, and pull a PDF edition when you want an offline copy.';
  }

  return truncateText(stripMarkdown(description), maxLength);
};

const setMetaTagContent = (selector: string, content: string) => {
  const element = document.querySelector<HTMLMetaElement>(selector);
  if (element) {
    element.setAttribute('content', content);
  }
};

const setCanonicalHref = (href: string) => {
  const element = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (element) {
    element.setAttribute('href', href);
  }
};

const getVideoTimestamp = (video: HomepageVideo): number => {
  const timestamp = new Date(video.publishedAt || video.createdAt).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const BrandLockup: React.FC<{ inverted?: boolean }> = ({ inverted = false }) => (
  <div className="flex items-center gap-4">
    <div
      className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[1.35rem] shadow-brand ${
        inverted ? 'bg-white/10' : 'bg-brand-navy'
      }`}
    >
      <img
        src="/logos/combination_mark_yellow.svg"
        alt="Machine Learning Street Talk"
        className="h-8 w-auto"
      />
    </div>
    <div>
      <p className={`font-headlines text-2xl font-bold tracking-tight ${inverted ? 'text-brand-yellow' : 'brand-highlight-text'}`}>
        ReScript
      </p>
    </div>
  </div>
);

const VideoGallery: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [videos, setVideos] = useState<HomepageVideo[]>([]);
  const [offlineSessions, setOfflineSessions] = useState<OfflineSessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOfflineSessions, setIsLoadingOfflineSessions] = useState(true);
  const [isOfflineMode, setIsOfflineMode] = useState(() => (typeof navigator === 'undefined' ? false : !navigator.onLine));
  const [error, setError] = useState<string | null>(null);
  const [failedThumbnails, setFailedThumbnails] = useState<Set<string>>(new Set());
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [selectedVideoInfo, setSelectedVideoInfo] = useState<HomepageVideo | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<HomepageVideo[]>([]);
  const [searchResultTotal, setSearchResultTotal] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    document.title = HOME_TITLE;
    setMetaTagContent('meta[name="description"]', HOME_DESCRIPTION);
    setMetaTagContent('meta[property="og:title"]', 'Machine Learning Street Talk | ReScript Archive');
    setMetaTagContent('meta[property="og:description"]', HOME_SOCIAL_DESCRIPTION);
    setMetaTagContent('meta[property="og:url"]', 'https://app.rescript.info');
    setMetaTagContent('meta[property="og:image"]', HOME_SOCIAL_IMAGE);
    setMetaTagContent('meta[name="twitter:title"]', 'Machine Learning Street Talk | ReScript Archive');
    setMetaTagContent('meta[name="twitter:description"]', HOME_SOCIAL_DESCRIPTION);
    setMetaTagContent('meta[name="twitter:image"]', HOME_SOCIAL_IMAGE);
    setCanonicalHref('https://app.rescript.info');
  }, []);

  const loadOfflineSessions = useCallback(async () => {
    try {
      setIsLoadingOfflineSessions(true);
      const sessions = await listOfflineSessions();
      setOfflineSessions(sessions);
    } catch (err) {
      console.error('[VIDEO-GALLERY] Error loading offline sessions:', err);
      setOfflineSessions([]);
    } finally {
      setIsLoadingOfflineSessions(false);
    }
  }, []);

  const loadVideos = useCallback(async () => {
    if (!navigator.onLine) {
      setVideos([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/public/homepage-videos`);

      if (!response.ok) {
        throw new Error('Failed to load videos');
      }

      const data = await response.json();
      const sortedVideos = [...(data.videos || [])].sort((left, right) => getVideoTimestamp(right) - getVideoTimestamp(left));
      setVideos(sortedVideos);
    } catch (err) {
      console.error('[VIDEO-GALLERY] Error loading videos:', err);
      setError('Failed to load videos');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadVideos();
    void loadOfflineSessions();
  }, [loadOfflineSessions, loadVideos]);

  useEffect(() => {
    const syncOnlineStatus = () => {
      setIsOfflineMode(!navigator.onLine);
      if (navigator.onLine) {
        void loadVideos();
      }
      void loadOfflineSessions();
    };

    const handleOfflineSessionsUpdated = () => {
      void loadOfflineSessions();
    };

    window.addEventListener('online', syncOnlineStatus);
    window.addEventListener('offline', syncOnlineStatus);
    window.addEventListener('rescript-offline-sessions-updated', handleOfflineSessionsUpdated);

    return () => {
      window.removeEventListener('online', syncOnlineStatus);
      window.removeEventListener('offline', syncOnlineStatus);
      window.removeEventListener('rescript-offline-sessions-updated', handleOfflineSessionsUpdated);
    };
  }, [loadOfflineSessions, loadVideos]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    const query = debouncedSearchQuery.trim();

    if (query.length < 2) {
      setSearchResults([]);
      setSearchResultTotal(0);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();

    const runSearch = async () => {
      try {
        setIsSearching(true);
        setSearchError(null);

        const response = await fetch(
          `${API_BASE_URL}/api/public/homepage-search?q=${encodeURIComponent(query)}&limit=24`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error || payload?.message || 'Search failed');
        }

        const data = await response.json();
        setSearchResults(data.videos || []);
        setSearchResultTotal(typeof data.total === 'number' ? data.total : (data.videos || []).length);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        setSearchResults([]);
        setSearchResultTotal(0);
        setSearchError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    };

    void runSearch();

    return () => controller.abort();
  }, [debouncedSearchQuery]);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    const googleIdToken = credentialResponse.credential;
    if (!googleIdToken) {
      setLoginError('No token received from Google');
      return;
    }

    try {
      setIsLoggingIn(true);
      setLoginError(null);

      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ idToken: googleIdToken }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();
      await login(data.accessProfile || null);
      setShowLoginModal(false);
      navigate('/');
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleVideoClick = (shareToken: string) => {
    navigate(`/share/${shareToken}`);
  };

  const handleOfflineSessionClick = (offlineSession: OfflineSessionSummary) => {
    if (navigator.onLine && offlineSession.shareToken) {
      navigate(`/share/${offlineSession.shareToken}`);
      return;
    }

    if (offlineSession.shareToken) {
      navigate(`/session/${offlineSession.sessionId}?share=${offlineSession.shareToken}`);
      return;
    }

    navigate(`/session/${offlineSession.sessionId}`);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>, shareToken: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleVideoClick(shareToken);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDisplayDate = (video: HomepageVideo): string => formatDate(video.publishedAt || video.createdAt);

  const handleThumbnailError = (shareToken: string) => {
    setFailedThumbnails((prev) => new Set(prev).add(shareToken));
  };

  const getFallbackThumbnail = (thumbnailUrl: string | null): string | null => {
    if (!thumbnailUrl) return null;
    return thumbnailUrl.replace('maxresdefault', 'hqdefault');
  };

  const handleImageError = (
    event: React.SyntheticEvent<HTMLImageElement, Event>,
    video: HomepageVideo
  ) => {
    const fallback = getFallbackThumbnail(video.thumbnailUrl);
    const image = event.currentTarget;

    if (fallback && image.dataset.fallbackApplied !== 'true' && image.src !== fallback) {
      image.dataset.fallbackApplied = 'true';
      image.src = fallback;
      return;
    }

    handleThumbnailError(video.shareToken);
  };

  const pdfCount = videos.filter((video) => video.hasPdf).length;
  const notesCount = videos.filter((video) => Boolean(video.description)).length;
  const featuredVideo = videos[0] ?? null;
  const hasOfflineSessions = offlineSessions.length > 0;
  const offlineSessionMap = useMemo(
    () => new Map(offlineSessions.map((session) => [session.sessionId, session])),
    [offlineSessions]
  );
  const activeSearchQuery = debouncedSearchQuery.trim();
  const isSearchActive = activeSearchQuery.length >= 2;
  const displayVideos = isSearchActive ? searchResults : videos;
  const archiveItemsAvailable = isOfflineMode ? offlineSessions.length > 0 : displayVideos.length > 0;
  const shellClassName =
    'brand-shell brand-grid bg-[#F4F4F4] text-[#232E3F] dark:bg-[#101722] dark:text-[#F4F4F4]';
  const heroTitleClassName = isDarkMode
    ? 'brand-highlight-text mt-6 max-w-4xl font-headlines text-5xl font-bold tracking-tight sm:text-6xl xl:text-7xl'
    : 'mt-6 max-w-4xl font-headlines text-5xl font-bold tracking-tight text-[#232E3F] [text-shadow:0_14px_34px_rgba(35,46,63,0.14)] sm:text-6xl xl:text-7xl';
  const featuredTitleClassName = isDarkMode
    ? 'brand-highlight-text mt-6 font-headlines text-3xl font-bold leading-tight'
    : 'mt-6 font-headlines text-3xl font-bold leading-tight text-[#232E3F]';
  const noteProseClassName = isDarkMode
    ? 'prose prose-sm prose-invert max-w-none prose-headings:text-brand-yellow prose-p:text-[#FFF5DE] prose-p:opacity-80 prose-strong:text-[#FFF5DE] prose-li:text-[#FFF5DE] prose-li:opacity-80 prose-a:text-brand-yellow prose-blockquote:text-brand-cloud prose-hr:border-white/10'
    : 'prose prose-sm max-w-none prose-headings:text-[#232E3F] prose-p:text-[#3E4B5E] prose-strong:text-[#232E3F] prose-li:text-[#3E4B5E] prose-a:text-[#8B5B00] prose-blockquote:text-[#4C586B] prose-hr:border-brand-navy/10';
  const archiveStats = [
    { label: 'Public Episodes', value: String(videos.length).padStart(2, '0') },
    { label: 'PDF Editions', value: String(pdfCount).padStart(2, '0') },
    { label: 'Episode Notes', value: String(notesCount).padStart(2, '0') },
  ];

  if ((isLoading || isLoadingOfflineSessions) && !hasOfflineSessions) {
    return (
      <div className={shellClassName}>
        <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-16 sm:px-6">
          <div className="brand-card w-full max-w-xl p-8 text-center sm:p-10">
            <div className="mx-auto mb-8 flex justify-center">
              <BrandLockup />
            </div>
            <div className="mx-auto brand-spinner" />
            <h1 className="mt-8 font-headlines text-4xl font-bold tracking-tight text-brand-navy dark:text-brand-cloud sm:text-5xl">
              Loading the archive
            </h1>
            <p className="mt-4 text-base leading-8 text-brand-navy/68 dark:text-brand-cloud/72 sm:text-lg">
              Pulling the latest MLST transcript editions and support materials.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !hasOfflineSessions && !isOfflineMode) {
    return (
      <div className={shellClassName}>
        <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-16 sm:px-6">
          <div className="brand-card w-full max-w-xl p-8 text-center sm:p-10">
            <div className="mx-auto mb-8 flex justify-center">
              <BrandLockup />
            </div>
            <p className="brand-kicker">
              <Sparkles className="h-3.5 w-3.5" />
              Archive Status
            </p>
            <h1 className="mt-6 font-headlines text-4xl font-bold tracking-tight text-brand-navy dark:text-brand-cloud sm:text-5xl">
              The public archive did not load
            </h1>
            <p className="mt-4 text-base leading-8 text-brand-navy/68 dark:text-brand-cloud/72 sm:text-lg">{error}</p>
            <div className="mt-8 flex justify-center">
              <button onClick={() => void loadVideos()} className="brand-button-primary">
                Try again
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClassName}>
      <div className="relative z-10">
        <header className="mx-auto max-w-7xl px-4 pt-5 sm:px-6 lg:px-8">
          <div className="brand-card px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <BrandLockup />
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="https://www.mlst.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brand-button-secondary"
                >
                  <BookOpen className="h-4 w-4" />
                  Substack
                </a>
                <button
                  onClick={() => navigate('/questions')}
                  className="brand-button-secondary"
                >
                  <MessageCircle className="h-4 w-4" />
                  Questions
                </button>
                <button
                  onClick={toggleDarkMode}
                  className="brand-button-secondary px-4"
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-pressed={isDarkMode}
                >
                  {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  <span className="hidden sm:inline">{isDarkMode ? 'Light' : 'Dark'}</span>
                </button>
                <button onClick={() => setShowLoginModal(true)} className="brand-button-primary">
                  <LogIn className="h-4 w-4" />
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pt-14">
          {(isOfflineMode || hasOfflineSessions) && (
            <section className="mb-10">
              <div className="brand-card brand-card-muted p-5 sm:p-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="brand-kicker">{isOfflineMode ? 'Offline Access' : 'Saved On This Device'}</p>
                    <h2 className="brand-highlight-text mt-4 font-headlines text-3xl font-bold tracking-tight sm:text-4xl">
                      {isOfflineMode ? 'Showing saved sessions only' : `${offlineSessions.length} saved session${offlineSessions.length === 1 ? '' : 's'}`}
                    </h2>
                    <p className="mt-3 max-w-3xl text-sm leading-7 text-brand-navy/64 dark:text-brand-cloud/72">
                      {isOfflineMode
                        ? 'The archive below is showing only sessions stored on this device. Video is not available offline.'
                        : 'Saved sessions stay in the normal archive flow. Look for the offline badge on any episode you have stored locally.'}
                    </p>
                  </div>
                  {isOfflineMode && (
                    <p className="max-w-md text-sm leading-7 text-brand-navy/56 dark:text-brand-cloud/64 lg:text-right">
                      Open any saved episode below for transcript, description, comments, and audio access without connectivity.
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}

          <section className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_380px] lg:items-end">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              <p className="brand-kicker">
                <Sparkles className="h-3.5 w-3.5" />
                Editorial Transcript Archive
              </p>
              <h1 className={heroTitleClassName}>
                <span
                  className={
                    isDarkMode
                      ? undefined
                      : 'rounded-[0.35em] bg-[linear-gradient(180deg,transparent_64%,rgba(255,181,43,0.24)_64%)] px-1 py-1 [box-decoration-break:clone] [-webkit-box-decoration-break:clone]'
                  }
                >
                  "The Machine Learning and AI Podcast That Actually Goes Deep."
                </span>
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-brand-navy/72 dark:text-brand-cloud/72 sm:text-xl">
                Browse the best Machine Learning Street Talk interviews through searchable transcripts,
                rich episode notes, and downloadable PDF editions. Hosted by Dr. Tim Scarfe.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="https://discord.gg/HNnAwSduud"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brand-button-primary"
                >
                  <MessageCircle className="h-4 w-4" />
                  Join Discord
                </a>
                <a
                  href="https://www.patreon.com/mlst"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brand-button-secondary"
                >
                  <Heart className="h-4 w-4" />
                  Patreon
                </a>
                <a
                  href="https://www.paypal.com/donate/?hosted_button_id=K2TYRVPBGXVNA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brand-button-ghost"
                >
                  <DollarSign className="h-4 w-4" />
                  Donate
                </a>
                <button
                  onClick={() => navigate('/questions')}
                  className="brand-button-secondary"
                >
                  <MessageCircle className="h-4 w-4" />
                  Explore questions
                </button>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {archiveStats.map((stat) => (
                  <div key={stat.label} className="brand-card brand-card-muted p-5 sm:p-6">
                    <p className="font-headlines text-[0.68rem] font-bold uppercase tracking-[0.28em] text-brand-navy/52 dark:text-brand-cloud/56">
                      {stat.label}
                    </p>
                    <p className="mt-4 font-headlines text-4xl font-bold tracking-tight text-brand-navy dark:text-[#FFF5DE]">
                      {stat.value}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.aside
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.08 }}
              className="brand-card overflow-hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,245,222,0.92))] p-6 text-brand-navy dark:bg-brand-navy dark:text-brand-cloud sm:p-8"
            >
              <p className="brand-kicker border-[#E4D4AB] bg-[#FFF3D6] text-[#7C5A0C] dark:border-white/15 dark:bg-white/10 dark:text-brand-cloud">
                Latest Edition
              </p>
              {featuredVideo ? (
                <>
                  <h2 className={featuredTitleClassName}>{featuredVideo.title}</h2>
                  <p className="mt-4 text-sm leading-7 text-brand-navy/68 dark:text-brand-cloud/74">
                    {getDescriptionPreview(featuredVideo.description, 230)}
                  </p>

                  <div className="mt-8 grid gap-3 text-sm text-brand-navy/68 dark:text-brand-cloud/74">
                    <div className="flex items-center justify-between gap-4 rounded-[1.25rem] border border-brand-navy/10 bg-white/86 px-4 py-3 dark:border-white/10 dark:bg-white/[0.08]">
                      <span className="font-headlines text-[0.68rem] font-bold uppercase tracking-[0.24em] text-brand-navy/48 dark:text-brand-cloud/58">
                        Published
                      </span>
                      <span>{getDisplayDate(featuredVideo)}</span>
                    </div>
                    {offlineSessionMap.has(featuredVideo.sessionId) && (
                      <div className="flex items-center justify-between gap-4 rounded-[1.25rem] border border-brand-navy/10 bg-white/86 px-4 py-3 dark:border-white/10 dark:bg-white/[0.08]">
                        <span className="font-headlines text-[0.68rem] font-bold uppercase tracking-[0.24em] text-brand-navy/48 dark:text-brand-cloud/58">
                          Offline
                        </span>
                        <span>Saved on this device</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-4 rounded-[1.25rem] border border-brand-navy/10 bg-white/86 px-4 py-3 dark:border-white/10 dark:bg-white/[0.08]">
                      <span className="font-headlines text-[0.68rem] font-bold uppercase tracking-[0.24em] text-brand-navy/48 dark:text-brand-cloud/58">
                        Notes
                      </span>
                      <span>{featuredVideo.description ? 'Included' : 'Transcript only'}</span>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      onClick={() => handleVideoClick(featuredVideo.shareToken)}
                      className="brand-button-primary"
                    >
                      Open transcript
                      <ArrowRight className="h-4 w-4" />
                    </button>
                    <a
                      href={`${API_BASE_URL}/api/public/sessions/${featuredVideo.sessionId}/pdf`}
                      onClick={(event) => event.stopPropagation()}
                      className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-brand-navy/12 bg-white/92 transition duration-200 hover:-translate-y-0.5 hover:border-brand-navy/22 hover:bg-white hover:shadow-brand dark:border-white/12 dark:bg-white/10 dark:hover:border-white/24 dark:hover:bg-white/[0.16]"
                      title={featuredVideo.hasPdf ? 'Download PDF transcript' : 'Generate and download PDF transcript'}
                      aria-label={featuredVideo.hasPdf ? 'Download PDF transcript' : 'Generate and download PDF transcript'}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <PdfBadgeIcon isDarkMode={isDarkMode} className="h-8 w-8" />
                    </a>
                    {featuredVideo.description && (
                      <button
                        onClick={() => setSelectedVideoInfo(featuredVideo)}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-navy/12 bg-white/92 px-5 py-3 font-headlines text-sm font-bold uppercase tracking-[0.18em] text-brand-navy transition duration-200 hover:-translate-y-0.5 hover:border-brand-navy/22 hover:bg-white hover:shadow-brand dark:border-white/12 dark:bg-white/10 dark:text-brand-cloud dark:hover:border-white/24 dark:hover:bg-white/[0.16]"
                      >
                        <FileText className="h-4 w-4" />
                        Read note
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <p className="mt-6 text-sm leading-7 text-brand-navy/68 dark:text-brand-cloud/74">
                  No public episode has been published yet. Check back shortly for the first transcript edition.
                </p>
              )}
            </motion.aside>
          </section>

          <section className="mt-16">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="brand-kicker">{isOfflineMode ? 'Saved Episodes' : 'Browse The Archive'}</p>
                <h2 className="brand-highlight-text mt-4 font-headlines text-3xl font-bold tracking-tight sm:text-4xl">
                  {isOfflineMode ? 'Saved episodes' : 'Public episodes'}
                </h2>
              </div>
              <p className="max-w-xl text-sm leading-7 text-brand-navy/62 dark:text-brand-cloud/68 sm:text-right">
                {isOfflineMode
                  ? 'Saved episodes use the same archive layout, with an offline badge marking anything available on this device.'
                  : 'Every card opens a full transcript view. Use the note button and download icon for deeper reading without leaving the archive.'}
              </p>
            </div>

            {!isOfflineMode && (
            <div className="brand-card brand-card-muted mt-8 p-4 sm:p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                <label className="relative block flex-1">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-navy/45 dark:text-brand-cloud/45" />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search titles, speakers, transcript moments, and topics"
                    className="h-12 w-full rounded-full border border-brand-navy/10 bg-white pl-11 pr-12 font-body text-sm text-brand-navy outline-none transition focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/35 dark:border-white/12 dark:bg-white/10 dark:text-brand-cloud"
                    aria-label="Search public episodes"
                  />
                  <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
                    {isSearching && <Loader2 className="h-4 w-4 animate-spin text-brand-navy/45 dark:text-brand-cloud/45" />}
                    {searchQuery && !isSearching && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-brand-navy/52 transition hover:bg-brand-navy/6 hover:text-brand-navy dark:text-brand-cloud/52 dark:hover:bg-white/10 dark:hover:text-brand-cloud"
                        aria-label="Clear episode search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </label>

                <div className="flex flex-col gap-1 text-sm text-brand-navy/62 dark:text-brand-cloud/68 lg:max-w-sm lg:text-right">
                  <p>Search the public archive by title, speaker names, transcript text, and indexed topics.</p>
                  <p className="text-xs uppercase tracking-[0.22em] text-brand-navy/45 dark:text-brand-cloud/48">
                    {isSearchActive
                      ? `${searchResultTotal} result${searchResultTotal === 1 ? '' : 's'} for "${activeSearchQuery}"`
                      : 'Type at least 2 characters'}
                  </p>
                </div>
              </div>

              {searchError && (
                <p className="mt-3 text-sm text-[#9f4b00] dark:text-brand-yellow">
                  {searchError}
                </p>
              )}
            </div>
            )}

            {!archiveItemsAvailable ? (
              <div className="brand-card mt-8 p-10 text-center">
                <Youtube className="mx-auto h-12 w-12 text-brand-navy/35" />
                <h3 className="brand-highlight-text mt-6 font-headlines text-2xl font-bold tracking-tight">
                  {isOfflineMode
                    ? 'No sessions saved offline yet'
                    : isSearchActive
                      ? 'No public episodes matched'
                      : 'No public episodes yet'}
                </h3>
                <p className="mt-3 text-base leading-8 text-brand-navy/65 dark:text-brand-cloud/72">
                  {isOfflineMode
                    ? 'Open a public share while online and use the save offline action to keep its transcript, comments, description, and audio on this device.'
                    : isSearchActive
                      ? `Try a broader query or search for a speaker, transcript phrase, or topic instead of "${activeSearchQuery}".`
                      : 'The archive is empty for now. Check back soon for the next transcript release.'}
                </p>
              </div>
            ) : (
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                <AnimatePresence>
                  {isOfflineMode ? offlineSessions.map((offlineSession, index) => (
                    <motion.article
                      key={offlineSession.sessionId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.04 }}
                      className="group h-full"
                      onClick={() => handleOfflineSessionClick(offlineSession)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleOfflineSessionClick(offlineSession);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="brand-card brand-card-muted flex h-full flex-col overflow-hidden transition duration-300 group-hover:-translate-y-1.5 group-hover:shadow-brand-lg">
                        <div className="relative aspect-[16/10] overflow-hidden bg-brand-navy/10">
                          {offlineSession.thumbnailUrl ? (
                            <img
                              src={getOfflineThumbnailCacheUrl(offlineSession.sessionId)}
                              alt={offlineSession.title}
                              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-brand-navy/12">
                              <Youtube className="h-14 w-14 text-brand-navy/32" />
                            </div>
                          )}

                          <div className="absolute inset-0 brand-scrim transition-opacity duration-300 group-hover:opacity-95" />
                          <div className="absolute inset-0 brand-thumbnail-grain transition-opacity duration-300 group-hover:opacity-100" />
                          <div className="absolute left-4 top-4 flex items-center gap-2">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-yellow text-brand-navy shadow-[0_16px_26px_-22px_rgba(255,181,43,0.95)]">
                              <Youtube className="h-4 w-4" />
                            </span>
                            <span className="rounded-full bg-white/92 px-3 py-1 font-headlines text-[0.62rem] font-bold uppercase tracking-[0.22em] text-brand-navy">
                              Saved Offline
                            </span>
                          </div>
                          <div className="absolute right-4 top-4 rounded-full bg-white/92 px-4 py-2 font-headlines text-[0.68rem] font-bold uppercase tracking-[0.24em] text-brand-navy opacity-0 transition duration-300 group-hover:opacity-100">
                            Open
                          </div>
                          <div className="absolute inset-x-0 bottom-0 p-5">
                            <h3 className="line-clamp-2 font-headlines text-2xl font-bold leading-tight text-brand-cloud">
                              {offlineSession.title}
                            </h3>
                            <div className="mt-3 flex items-center gap-2 text-[0.7rem] font-headlines uppercase tracking-[0.28em] text-white/72">
                              <Calendar className="h-3.5 w-3.5" />
                              {formatDate(offlineSession.savedAt)}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-1 flex-col p-5 sm:p-6">
                          <p className="line-clamp-4 text-sm leading-7 text-brand-navy/68 dark:text-brand-cloud/74">
                            {offlineSession.descriptionPreview || offlineSession.transcriptPreview || 'Transcript, description, comments, and audio are available offline on this device.'}
                          </p>

                          <div className="mt-6 flex items-center justify-between gap-3">
                            <span className="text-xs uppercase tracking-[0.2em] text-brand-navy/45 dark:text-brand-cloud/48">
                              {offlineSession.duration > 0 ? `${Math.round(offlineSession.duration / 60)} min` : 'Transcript only'}
                            </span>
                            <span className="inline-flex items-center gap-2 font-headlines text-[0.72rem] font-bold uppercase tracking-[0.24em] text-[#A36A00] dark:text-brand-yellow">
                              Open session
                              <ArrowRight className="h-4 w-4" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  )) : displayVideos.map((video, index) => (
                    <motion.article
                      key={video.shareToken}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.04 }}
                      className="group h-full"
                      onClick={() => handleVideoClick(video.shareToken)}
                      onKeyDown={(event) => handleCardKeyDown(event, video.shareToken)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="brand-card brand-card-muted flex h-full flex-col overflow-hidden transition duration-300 group-hover:-translate-y-1.5 group-hover:shadow-brand-lg">
                        <div className="relative aspect-[16/10] overflow-hidden bg-brand-navy/10">
                          {video.thumbnailUrl && !failedThumbnails.has(video.shareToken) ? (
                            <img
                              src={video.thumbnailUrl}
                              alt={video.title}
                              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                              loading="lazy"
                              onError={(event) => handleImageError(event, video)}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-brand-navy/12">
                              <Youtube className="h-14 w-14 text-brand-navy/32" />
                            </div>
                          )}

                          <div className="absolute inset-0 brand-scrim transition-opacity duration-300 group-hover:opacity-95" />
                          <div className="absolute inset-0 brand-thumbnail-grain transition-opacity duration-300 group-hover:opacity-100" />

                          <div className="absolute left-4 top-4 flex items-center gap-2">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-yellow text-brand-navy shadow-[0_16px_26px_-22px_rgba(255,181,43,0.95)]">
                              {video.youtubeUrl ? (
                                <Youtube className="h-4 w-4" />
                              ) : (
                                <Film className="h-4 w-4" />
                              )}
                            </span>
                            <span className="rounded-full bg-white/92 px-3 py-1 font-headlines text-[0.62rem] font-bold uppercase tracking-[0.22em] text-brand-navy">
                              {video.youtubeUrl ? 'YouTube' : 'Hosted'}
                            </span>
                            {offlineSessionMap.has(video.sessionId) && (
                              <span className="rounded-full bg-white/92 px-3 py-1 font-headlines text-[0.62rem] font-bold uppercase tracking-[0.22em] text-brand-navy">
                                Saved Offline
                              </span>
                            )}
                          </div>

                          <div className="absolute right-4 top-4 rounded-full bg-white/92 px-4 py-2 font-headlines text-[0.68rem] font-bold uppercase tracking-[0.24em] text-brand-navy opacity-0 transition duration-300 group-hover:opacity-100">
                            Open
                          </div>

                          <div className="absolute inset-x-0 bottom-0 p-5">
                            <h3 className="line-clamp-2 font-headlines text-2xl font-bold leading-tight text-brand-cloud">
                              {video.title}
                            </h3>
                            <div className="mt-3 flex items-center gap-2 text-[0.7rem] font-headlines uppercase tracking-[0.28em] text-white/72">
                              <Calendar className="h-3.5 w-3.5" />
                              {getDisplayDate(video)}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-1 flex-col p-5 sm:p-6">
                          <p className="line-clamp-3 text-sm leading-7 text-brand-navy/68 dark:text-brand-cloud/74">
                            {isSearchActive
                              ? (video.searchPreview || getDescriptionPreview(video.description, 165))
                              : getDescriptionPreview(video.description, 165)}
                          </p>

                          {isSearchActive && Array.isArray(video.matchedFields) && video.matchedFields.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {video.matchedFields
                                .map((field) => SEARCH_MATCH_LABELS[field] || field)
                                .filter((value, fieldIndex, values) => values.indexOf(value) === fieldIndex)
                                .slice(0, 3)
                                .map((label) => (
                                  <span
                                    key={`${video.sessionId}-${label}`}
                                    className="inline-flex rounded-full border border-brand-navy/10 bg-white px-3 py-1 font-headlines text-[0.62rem] font-bold uppercase tracking-[0.2em] text-brand-navy/70 dark:border-white/12 dark:bg-white/10 dark:text-brand-cloud/72"
                                  >
                                    {label}
                                  </span>
                                ))}
                            </div>
                          )}

                          <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
                            <div className="flex items-center gap-2">
                              <a
                                href={`${API_BASE_URL}/api/public/sessions/${video.sessionId}/pdf`}
                                onClick={(event) => event.stopPropagation()}
                                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-navy/10 bg-white px-2 py-2 transition duration-200 hover:-translate-y-0.5 hover:border-brand-navy/20 hover:shadow-brand dark:border-white/12 dark:bg-white/10"
                                title={video.hasPdf ? 'Download PDF transcript' : 'Generate and download PDF transcript'}
                                aria-label={video.hasPdf ? 'Download PDF transcript' : 'Generate and download PDF transcript'}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <PdfBadgeIcon isDarkMode={isDarkMode} className="h-8 w-8" />
                              </a>

                              {video.description && (
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedVideoInfo(video);
                                  }}
                                  className="inline-flex items-center gap-2 rounded-full border border-brand-navy/10 bg-white px-3 py-2 font-headlines text-[0.68rem] font-bold uppercase tracking-[0.24em] text-brand-navy transition duration-200 hover:-translate-y-0.5 hover:border-brand-navy/20 hover:shadow-brand dark:border-white/12 dark:bg-white/10 dark:text-brand-cloud dark:hover:border-white/20"
                                  title="View description"
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                  <span className="hidden sm:inline">Notes</span>
                                </button>
                              )}
                            </div>
                          </div>

                          {video.description && (
                            <div className="sr-only" aria-hidden="false">
                              <section>
                                <h4>Description</h4>
                                <p>{video.description}</p>
                              </section>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </section>

          <footer className="mt-16">
            <div className="brand-divider" />
            <div className="flex flex-col gap-3 py-8 text-sm text-[#B57700] dark:text-brand-yellow sm:flex-row sm:items-center sm:justify-between">
              <p>Interested in licensing ReScript for your podcast or research archive?</p>
              <a href={`mailto:${LICENSING_EMAIL}`} className="brand-link font-headlines text-[0.78rem] uppercase tracking-[0.28em] text-[#C98A00] dark:text-brand-yellow">
                {LICENSING_EMAIL}
              </a>
            </div>
          </footer>
        </main>
      </div>

      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#171d29]/72 p-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="brand-card w-full max-w-4xl overflow-hidden"
          >
            <div className="grid gap-0 md:grid-cols-[0.95fr_1.05fr]">
              <div className="bg-brand-navy px-6 py-8 text-brand-cloud sm:px-8 sm:py-10">
                <button
                  onClick={() => setShowLoginModal(false)}
                  className="ml-auto flex rounded-full border border-white/10 p-2 text-brand-cloud/70 transition duration-200 hover:border-white/20 hover:text-brand-cloud"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="mt-8">
                  <BrandLockup inverted />
                </div>
                <p className="mt-8 brand-kicker border-white/15 bg-white/10 text-brand-cloud">Member Access</p>
                <h2 className="mt-6 font-headlines text-4xl font-bold tracking-tight text-brand-cloud">
                  Sign in for the full MLST workspace.
                </h2>
                <p className="mt-4 text-base leading-8 text-brand-cloud/72">
                  Google sign-in unlocks the private workflow: organization access, editing tools, and the full
                  ReScript surface beyond the public transcript archive.
                </p>
              </div>

              <div className="bg-white p-6 sm:p-8 md:p-10 dark:bg-[#162232]/[0.94]">
                <p className="brand-kicker">Authentication</p>
                <h3 className="brand-highlight-text mt-6 font-headlines text-3xl font-bold tracking-tight">
                  Continue with Google
                </h3>
                <p className="mt-4 text-base leading-8 text-brand-navy/68 dark:text-brand-cloud/72">
                  Use the same Google account your organization admin has approved for access.
                </p>

                {loginError && (
                  <div className="mt-6 rounded-[1.25rem] border border-red-300/70 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {loginError}
                  </div>
                )}

                <div className="mt-8 flex flex-col items-start gap-4">
                  {isLoggingIn ? (
                    <div className="flex items-center gap-3 rounded-full border border-brand-navy/10 bg-white px-5 py-3 text-sm text-brand-navy/70 dark:border-white/12 dark:bg-white/10 dark:text-brand-cloud/76">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Authenticating...
                    </div>
                  ) : (
                    <>
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setLoginError('Google authentication failed')}
                        theme="outline"
                        shape="pill"
                        size="large"
                        text="continue_with"
                        logo_alignment="left"
                      />
                      {isDevelopment && (
                        <button
                          onClick={async () => {
                            try {
                              setIsLoggingIn(true);
                              setLoginError(null);
                              const response = await fetch(`${API_BASE_URL}/api/auth/dev-login`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                              });

                              if (!response.ok) throw new Error('Dev login failed');

                              const data = await response.json();
                              await login(data.accessProfile || null);
                              setShowLoginModal(false);
                              navigate('/');
                            } catch (err) {
                              setLoginError(err instanceof Error ? err.message : 'Dev login failed');
                            } finally {
                              setIsLoggingIn(false);
                            }
                          }}
                          className="brand-button-secondary"
                        >
                          Dev login
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {selectedVideoInfo && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-hidden bg-[#232E3F]/38 backdrop-blur-sm dark:bg-[#171d29]/72 sm:items-center"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
            paddingRight: 'calc(env(safe-area-inset-right, 0px) + 1rem)',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
            paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 1rem)',
          }}
          onClick={() => setSelectedVideoInfo(null)}
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="brand-card flex max-h-full w-full max-w-3xl flex-col overflow-hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,245,222,0.94))] text-brand-navy dark:border-white/10 dark:bg-brand-navy dark:text-brand-cloud"
            style={{
              maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 2rem)',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-brand-navy/10 px-5 py-5 dark:border-white/10 sm:px-8 sm:py-6">
              <div className="min-w-0">
                <p className="brand-kicker border-[#E4D4AB] bg-[#FFF3D6] text-[#7C5A0C] dark:border-white/15 dark:bg-white/10 dark:text-brand-cloud">
                  Episode Note
                </p>
                <h2 className="mt-4 pr-3 font-headlines text-2xl font-bold tracking-tight text-[#232E3F] dark:text-brand-yellow sm:pr-6 sm:text-3xl">
                  {selectedVideoInfo.title}
                </h2>
                <p className="mt-3 text-sm uppercase tracking-[0.24em] text-brand-navy/48 dark:text-brand-cloud/55">
                  {getDisplayDate(selectedVideoInfo)}
                </p>
              </div>
              <button
                onClick={() => setSelectedVideoInfo(null)}
                className="rounded-full border border-brand-navy/10 p-2 text-brand-navy/55 transition duration-200 hover:border-brand-navy/20 hover:text-brand-navy dark:border-white/10 dark:text-brand-cloud/70 dark:hover:border-white/20 dark:hover:text-brand-cloud"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-8 sm:py-6">
              {selectedVideoInfo.description ? (
                <div className={noteProseClassName}>
                  <ReactMarkdown>{selectedVideoInfo.description}</ReactMarkdown>
                </div>
              ) : (
                <p className="py-8 text-center text-brand-navy/55 dark:text-brand-cloud/60">
                  No description available for this episode.
                </p>
              )}
            </div>

            <div className="shrink-0 border-t border-brand-navy/10 bg-brand-navy/[0.03] px-5 py-4 dark:border-white/10 dark:bg-white/[0.04] sm:px-8">
              <button
                onClick={() => {
                  setSelectedVideoInfo(null);
                  handleVideoClick(selectedVideoInfo.shareToken);
                }}
                className="brand-button-primary w-full"
              >
                <ExternalLink className="h-4 w-4" />
                View full transcript
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default VideoGallery;
